import { useMemo } from 'react';
import { ColumnDescription } from 'react-bootstrap-table-next';

import { ItemImportStateTypes, PlanFeaturesTypes } from '~globals/types/enums';
import { PartnerCompanyItem } from '~services/partnerCompany/types';
import {
  OrderClientInfo,
  OrderMFlexQR,
  SkillsListInfo,
  ExpirationDateInfo,
  CapabilitiesInfo,
  OrderReceiveStateChip,
} from '~components/index';
import { formatterDate } from '~utils/formatter';

import DashboardVariantV2CarrierContentCollectTabDataTableActionsMenu, {
  DashboardVariantV2CarrierContentCollectTabDataTableActionsMenuProps,
} from './ActionsMenu';

interface UseCollectTabDataTableColumnsParams
  extends Omit<
    DashboardVariantV2CarrierContentCollectTabDataTableActionsMenuProps,
    'data'
  > {
  showMap: boolean;
}

export const useCollectTabDataTableColumns = ({
  showMap,
}: UseCollectTabDataTableColumnsParams): ColumnDescription<PartnerCompanyItem>[] => {
  const columns = useMemo<ColumnDescription<PartnerCompanyItem>[]>(
    () => [
      {
        dataField: 'title',
        text: 'Nombre de contacto',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <OrderClientInfo
            name={row.title}
            itemTypeId={row.itemTypeId}
            trackingCode={row.trackingCode}
            hasError={row.itemImportStateTypeId === ItemImportStateTypes.Error}
          />
        ),
        headerStyle: { width: 180 },
      },
      {
        dataField: 'originAddress',
        text: 'Dirección',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: showMap,
        headerStyle: { width: 300 },
      },
      {
        dataField: 'referenceId',
        text: 'Nro. de Orden',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        headerStyle: { width: 120 },
      },
      {
        dataField: 'qrcode',
        text: 'QR',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => {
          if (!row.qrcode) return null;

          return (
            <OrderMFlexQR.IconWithTooltipInfo
              qrData={row.qrcode}
              referenceId={row.referenceId}
            />
          );
        },
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitPaid,
        },
        headerAlign: 'center',
        align: 'center',
        hidden: true,
        headerStyle: { width: 60 },
      },
      {
        dataField: 'tags',
        text: 'Grupo',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        headerStyle: { width: 120 },
      },
      {
        dataField: 'skillsNeeded',
        text: 'Cargas especiales',
        classes: 'truncated-two-lines',
        formatter: (cell) => <SkillsListInfo skills={cell} />,
        align: 'center',
        headerStyle: { width: 100 },
        hidden: showMap,
      },
      {
        dataField: 'giverCompanyName',
        text: 'Empresa',
        classes: 'truncated-two-lines',
        formatter: (cell) => <span>{cell}</span>,
        hidden: showMap,
        headerStyle: { width: 150 },
      },
      {
        dataField: 'creationDateTime',
        text: 'Fecha de creación',
        classes: 'truncated-two-lines',
        formatter: (cell) => formatterDate(cell, { format: 'DD/MM/YYYY' }),
        hidden: true,
        headerStyle: { width: 150 },
      },
      {
        dataField: 'maxDeliveryDateTime',
        text: 'Fecha de vencimiento',
        classes: 'truncated-two-lines',
        formatter: (cell) => <ExpirationDateInfo expirationDate={cell} />,
        hidden: showMap,
        headerStyle: { width: 150 },
      },
      {
        dataField: 'contactPhone',
        text: 'Telefono',
        classes: 'truncated-two-lines',
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'contactMobile',
        text: 'Celular / Whatsapp',
        classes: 'truncated-two-lines',
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'unit1',
        text: 'Capacidades',
        formatter: (_cell, row) => (
          <CapabilitiesInfo
            capacity1={row.unit1 * row.amount}
            capacity2={row.unit2 * row.amount}
            numberOfPackages={row.numberOfPackages}
            timeWindow={{
              allDay: row.timeWindowAllDay,
              timeFrom: row.timeWindowFrom1,
              timeTo: row.timeWindowTo1,
              timeFrom2: row.timeWindowFrom2,
              timeTo2: row.timeWindowTo2,
            }}
            maximumVisits={null}
            fontSize={12}
            fontWeight="bold"
          />
        ),
        hidden: true,
        headerStyle: { width: 200 },
      },
      {
        dataField: 'customField1',
        text: 'Campo personalizado 1',
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'customField2',
        text: 'Campo personalizado 2',
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'customField3',
        text: 'Campo personalizado 3',
        hidden: true,
        headerStyle: { width: 160 },
      },
      {
        dataField: 'collectItemStateTypeId',
        isDummyField: true,
        text: 'Estado',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <OrderReceiveStateChip
            collectItemStateTypeId={row.collectItemStateTypeId}
          />
        ),
        headerAlign: 'center',
        align: 'center',
        headerStyle: { width: 170 },
      },
      {
        dataField: 'actions',
        isDummyField: true,
        text: '',
        classes: 'truncated-two-lines',
        formatter: (_cell, row) => (
          <DashboardVariantV2CarrierContentCollectTabDataTableActionsMenu
            data={row}
          />
        ),
        headerStyle: { width: 60 },
      },
    ],
    [showMap],
  );

  return columns;
};
